import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { AuthLayout, AuthFormActions, AuthFooterLinks } from '@/components/auth'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { userService } from '@/services'
import { forgotPasswordSchema, ForgotPasswordFormValues } from '@/lib/validations/auth'

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setIsLoading(true)
    try {
      await userService.requestPasswordReset(data.email)
      setEmailSent(true)
      toast.success('Password reset link sent to your email')
    } catch {
      toast.error('Failed to send reset link')
    } finally {
      setIsLoading(false)
    }
  }

  if (emailSent) {
    return (
      <AuthLayout
        title="Check Your Email"
        description="We've sent a password reset link to your email address"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Please check your email and follow the instructions to reset your password.
          </p>
          <Link to="/login">
            <Button className="w-full">Back to Log In</Button>
          </Link>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Forgot Password"
      description="Enter your email and we'll send you a link to reset your password"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            {...register('email')}
          />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>

        <AuthFormActions>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
          <AuthFooterLinks
            prefix="Remember your password?"
            links={[{ to: '/login', label: 'Log in' }]}
          />
        </AuthFormActions>
      </form>
    </AuthLayout>
  )
}
